import { useQuery } from '@tanstack/react-query';
import { Badge } from '../../../components/ui';
import { formatDateTime, formatQuantity, humanise } from '../../../lib/format';
import { recentRecoveriesQuery } from '../queries';
import {
  RowTitle,
  WidgetCard,
  WidgetEmpty,
  WidgetError,
  WidgetList,
  WidgetRow,
  WidgetSkeleton,
} from '../primitives';

/** Material coming back into inventory from production. */
export function RecentRecoveriesWidget({ limit = 5, to }: { limit?: number; to?: string }) {
  const query = useQuery(recentRecoveriesQuery);
  const rows = (query.data?.items ?? []).slice(0, limit);

  return (
    <WidgetCard
      title="Recent recoveries"
      description="Reclaimed material, back in inventory as ordinary stock."
      to={to}
      toLabel="All recoveries"
    >
      {query.isPending ? <WidgetSkeleton rows={2} /> : null}
      {query.isError ? (
        <WidgetError label="Could not load recoveries." onRetry={() => void query.refetch()} />
      ) : null}
      {query.isSuccess && rows.length === 0 ? (
        <WidgetEmpty>Nothing recovered yet.</WidgetEmpty>
      ) : null}
      {rows.length > 0 ? (
        <WidgetList>
          {rows.map((recovery) => (
            <WidgetRow key={recovery.id}>
              <RowTitle
                title={recovery.code}
                subtitle={
                  <>
                    <span className="font-mono">{recovery.itemCode}</span> ·{' '}
                    {formatDateTime(recovery.occurredAt)}
                  </>
                }
              />
              <div className="shrink-0 text-right">
                <p className="whitespace-nowrap text-sm font-semibold text-ink">
                  {formatQuantity(recovery.quantity, recovery.uom)}
                </p>
                <Badge tone={recovery.lotStatus === 'available' ? 'green' : 'hazard'}>
                  {humanise(recovery.lotStatus)}
                </Badge>
              </div>
            </WidgetRow>
          ))}
        </WidgetList>
      ) : null}
    </WidgetCard>
  );
}
